/**
 * usePlaybackControls.js
 *
 * Extracted from PlayerContext.js - handles play/pause, next/prev track,
 * seek, volume fade, shuffle, repeat, and playback rate controls.
 *
 * Provides: handlePlayPause, handleNextTrack, handlePrevTrack, handleSeek,
 *           setPlaybackState, fadeVolume, toggleShuffle, toggleRepeat, setPlaybackRate
 */
import { useCallback, useEffect } from 'react';
import logger from '../services/logger';
import { logUserAction } from '../utils/basicBehaviorLogger';
import ambientAudioManager from '../services/ambientAudioManager';

const ActionTypes = {
  SET_PLAYBACK_STATE: 'SET_PLAYBACK_STATE',
  SET_PLAYBACK_OPTION: 'SET_PLAYBACK_OPTION'
};

// Fisher-Yates shuffle on index array — keeps current track at front
const generateShuffleOrder = (trackCount, currentIndex) => {
  const indices = Array.from({ length: trackCount }, (_, i) => i);
  indices.splice(currentIndex, 1); // remove current from pool
  for (let i = indices.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [indices[i], indices[j]] = [indices[j], indices[i]];
  }
  return [currentIndex, ...indices]; // current track plays first, rest in random order
};

export function usePlaybackControls(state, dispatch, refs) {
  const {
    playerInstanceRef,
    lastActionTimeRef,
    segmentPlaybackRef,
    radioModeRef,
    producerFillScheduledRef,
    makeAuthenticatedRequest,
    setPlaybackOption
  } = refs;

  const setPlaybackState = useCallback((isPlaying) => {
    dispatch({ type: ActionTypes.SET_PLAYBACK_STATE, payload: isPlaying });
    ambientAudioManager.setMusicPlaying(isPlaying);
  }, [dispatch]);

  // Active fade interval ID — cancel previous fade when starting a new one
  const fadeIntervalRef = refs.fadeIntervalRef;

  // Smooth volume fade - shared function used by show open, segments, and transitions
  const fadeVolume = useCallback(async (targetVolume, duration = 1000) => {
    if (!playerInstanceRef.current) return;

    // Cancel any in-progress fade to prevent overlapping intervals
    if (fadeIntervalRef.current) {
      clearInterval(fadeIntervalRef.current);
      fadeIntervalRef.current = null;
    }

    try {
      const player = playerInstanceRef.current;

      if (typeof player.getVolume !== 'function' || typeof player.setVolume !== 'function') {
        logger.warn("Player doesn't support volume methods");
        return;
      }

      const currentVolume = await player.getVolume();
      const volumeDiff = targetVolume - currentVolume;

      if (Math.abs(volumeDiff) < 0.05) {
        await player.setVolume(targetVolume);
        return;
      }

      const steps = Math.max(duration / 50, 10);
      const volumeStep = volumeDiff / steps;

      return new Promise(resolve => {
        let step = 0;

        fadeIntervalRef.current = setInterval(async () => {
          step++;
          const newVolume = currentVolume + (volumeStep * step);

          try {
            await player.setVolume(Math.max(0, Math.min(1, newVolume)));
          } catch (e) {
            logger.error('Error setting volume:', e);
          }

          if (step >= steps) {
            await player.setVolume(targetVolume);
            clearInterval(fadeIntervalRef.current);
            fadeIntervalRef.current = null;
            resolve();
          }
        }, duration / steps);
      });
    } catch (e) {
      logger.error('Error in volume fade:', e);
    }
  }, [playerInstanceRef, fadeIntervalRef]);

  // Register fadeVolume with ambient manager so quips can duck music
  useEffect(() => {
    ambientAudioManager.setFadeVolume(fadeVolume);
  }, [fadeVolume]);

  // Regenerate shuffle order when track count changes while shuffle is active
  // Intentionally only watching tracks.length — we don't want to re-shuffle on every currentIndex change
  useEffect(() => {
    if (!state.shuffleMode || !state.playlist.tracks.length) return;
    setPlaybackOption('shuffleOrder', generateShuffleOrder(state.playlist.tracks.length, state.currentTrackIndex));
  }, [state.playlist.tracks.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePlayPause = useCallback(async () => {
    const now = Date.now();
    if (now - lastActionTimeRef.current < 300) return;
    lastActionTimeRef.current = now;

    const newPlayState = !state.isPlaying;
    setPlaybackState(newPlayState);

    if (state.platform === 'spotify') {
      if (playerInstanceRef.current) {
        if (newPlayState) {
          playerInstanceRef.current.resume()
            .catch(err => logger.error("Resume error:", err));
        } else {
          playerInstanceRef.current.pause()
            .catch(err => logger.error("Pause error:", err));
        }
      } else if (state.deviceId) {
        try {
          const endpoint = newPlayState ? 'play' : 'pause';
          await makeAuthenticatedRequest(
            `https://api.spotify.com/v1/me/player/${endpoint}?device_id=${state.deviceId}`,
            { method: 'PUT' },
            'spotify'
          );
        } catch (err) {
          logger.error(`API ${newPlayState ? 'play' : 'pause'} error:`, err);
        }
      }
    }
  }, [state.isPlaying, state.platform, state.deviceId, setPlaybackState, makeAuthenticatedRequest, playerInstanceRef, lastActionTimeRef]);

  const handleNextTrack = useCallback(() => {
    const now = Date.now();
    const timeSinceLastAction = now - lastActionTimeRef.current;

    logger.debug(`🎵 handleNextTrack CALLED: timeSinceLastAction=${timeSinceLastAction}ms, isChangingTrack=${window._playerStateFlags.isChangingTrack}, naturalProgression=${window._naturalProgression}`);

    if (now - lastActionTimeRef.current < 300) {
      logger.debug(`⏱️ DEBOUNCE BLOCK: Last action was ${timeSinceLastAction}ms ago (< 300ms threshold)`);
      return;
    }

    if (window._playerStateFlags.isChangingTrack) {
      logger.debug('🔒 ALREADY CHANGING TRACK: isChangingTrack flag is true, blocking');
      return;
    }

    // DJ BREAK PROTECTION: Block skip during placard prep or playback
    if (segmentPlaybackRef.current.isPlaying || segmentPlaybackRef.current.isPreloading) {
      logger.debug('🎙️ DJ BREAK BLOCK: Skip blocked during DJ break prep/playback');
      return;
    }

    // ENHANCED AI RADIO CONTROL: Check if action should be blocked
    if (radioModeRef.current && radioModeRef.current.shouldBlockUserAction) {
      const blockInfo = radioModeRef.current.shouldBlockUserAction('skip');
      logger.debug(`🎛️ Radio Mode Check: blocked=${blockInfo.blocked}, reason=${blockInfo.reason}`);
      // Allow natural progression to bypass skip blocking (for auto-advance)
      if (blockInfo.blocked && !window._naturalProgression) {
        logger.debug(`Skip blocked - ${blockInfo.reason}`);
        return;
      }
      if (blockInfo.blocked && window._naturalProgression) {
        logger.debug(`✅ BYPASSING RADIO BLOCK: Natural progression allows track change during DJ speech`);
      }
    }

    logger.debug('✅ handleNextTrack PROCEEDING: All checks passed');

    lastActionTimeRef.current = now;

    window._playerStateFlags.isChangingTrack = true;

    if (!state.playlist.tracks || state.playlist.tracks.length === 0) return;

    // PHASE 0: Log user skip action (not on natural track end — complete already logged)
    const currentTrack = state.playlist.tracks[state.currentTrackIndex];
    if (currentTrack && !window._naturalProgression) {
      logUserAction({
        action: 'skip',
        trackUri: currentTrack.uri,
        trackName: currentTrack.name,
        artist: currentTrack.artists || 'Unknown Artist',
        position: state.progressMs,
        timestamp: now
      });
    }

    let nextIndex;
    if (state.shuffleMode && state.shuffleOrder && state.shuffleOrder.length > 0) {
      const orderPos = state.shuffleOrder.indexOf(state.currentTrackIndex);
      if (orderPos < 0 || orderPos >= state.shuffleOrder.length - 1) {
        // At end of shuffle order
        if (state.repeatMode === 'context') {
          logger.debug('🔁 SHUFFLE END: Looping back to start of shuffle order');
          nextIndex = state.shuffleOrder[0];
        } else {
          logger.debug('🛑 SHUFFLE END: No more tracks, stopping');
          window._playerStateFlags.isChangingTrack = false;
          return;
        }
      } else {
        nextIndex = state.shuffleOrder[orderPos + 1];
        logger.debug(`🔀 SHUFFLE NEXT: order pos ${orderPos} → ${orderPos + 1}, track index ${nextIndex}`);
      }
    } else if (state.currentTrackIndex >= state.playlist.tracks.length - 1) {
      if (state.repeatMode === 'context') {
        logger.debug('🔁 END OF PLAYLIST: Looping to start (repeat context mode)');
        nextIndex = 0;
      } else {
        logger.debug('🛑 END OF PLAYLIST: No more tracks, stopping');
        window._playerStateFlags.isChangingTrack = false;
        return;
      }
    } else {
      nextIndex = state.currentTrackIndex + 1;
      logger.debug(`➡️ TRACK INDEX CHANGE: ${state.currentTrackIndex} → ${nextIndex}`);
    }

    // DJ breaks now trigger via placards in setProgress(), not here
    logger.debug(`🎯 SET TRACK INDEX: ${nextIndex} (${state.playlist.tracks[nextIndex]?.name})`);
    refs.setCurrentTrackIndex(nextIndex);

    // === AMBIENT: Producer fill at transition (radio mode only) ===
    if (radioModeRef.current?.isActive) {
      if (producerFillScheduledRef.current) {
        producerFillScheduledRef.current = false;
        ambientAudioManager.playClip('producer_fill');
      }
    }

    setTimeout(() => {
      window._playerStateFlags.isChangingTrack = false;
      logger.debug('✅ handleNextTrack COMPLETE: isChangingTrack flag cleared');
    }, 500);
  }, [state.currentTrackIndex, state.playlist.tracks, state.repeatMode, state.shuffleMode, state.shuffleOrder, state.progressMs, refs, lastActionTimeRef, segmentPlaybackRef, radioModeRef, producerFillScheduledRef]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePrevTrack = useCallback(() => {
    const now = Date.now();
    if (now - lastActionTimeRef.current < 300 || window._playerStateFlags.isChangingTrack) return;

    // DJ BREAK PROTECTION: Block during placard prep or playback
    if (segmentPlaybackRef.current.isPlaying || segmentPlaybackRef.current.isPreloading) {
      logger.debug('🎙️ DJ BREAK BLOCK: Previous blocked during DJ break prep/playback');
      return;
    }

    // ENHANCED AI RADIO CONTROL: Check if action should be blocked
    if (radioModeRef.current && radioModeRef.current.shouldBlockUserAction) {
      const blockInfo = radioModeRef.current.shouldBlockUserAction('previous');
      if (blockInfo.blocked) {
        logger.debug(`Previous track blocked - ${blockInfo.reason}`);
        return;
      }
    }

    lastActionTimeRef.current = now;

    if (!state.playlist.tracks || state.playlist.tracks.length === 0) return;

    if (state.progressMs < 3000) {
      // Within 3s — go to previous track
      window._playerStateFlags.isChangingTrack = true;
      refs.setCurrentTrackIndex(Math.max(0, state.currentTrackIndex - 1));
      setTimeout(() => {
        window._playerStateFlags.isChangingTrack = false;
      }, 500);
    } else {
      // Beyond 3s — restart current track from beginning
      if (playerInstanceRef.current?.seek) {
        playerInstanceRef.current.seek(0);
      }
    }

  }, [state.currentTrackIndex, state.progressMs, state.playlist.tracks, refs, lastActionTimeRef, segmentPlaybackRef, radioModeRef, playerInstanceRef]);

  // NOTE: handleSeek is defined in PlayerContext.js because it depends on djBreaks.setProgress

  const toggleShuffle = useCallback(() => {
    const newShuffleMode = !state.shuffleMode;
    setPlaybackOption('shuffleMode', newShuffleMode);
    if (newShuffleMode && state.playlist.tracks.length > 0) {
      setPlaybackOption('shuffleOrder', generateShuffleOrder(state.playlist.tracks.length, state.currentTrackIndex));
      logger.debug(`🔀 Shuffle ON — generated order for ${state.playlist.tracks.length} tracks`);
    } else {
      setPlaybackOption('shuffleOrder', null);
      logger.debug('🔀 Shuffle OFF — order cleared');
    }
  }, [state.shuffleMode, state.playlist.tracks.length, state.currentTrackIndex, setPlaybackOption]);

  const toggleRepeat = useCallback(() => {
    const modes = ['off', 'context', 'track'];
    const currentIndex = modes.indexOf(state.repeatMode);
    const nextIndex = (currentIndex + 1) % modes.length;
    setPlaybackOption('repeatMode', modes[nextIndex]);
  }, [state.repeatMode, setPlaybackOption]);

  const setPlaybackRate = useCallback((rate) => {
    setPlaybackOption('playbackRate', rate);

    if (playerInstanceRef.current) {
      try {
        if (state.platform === 'spotify') {
          logger.debug(`Set playback rate to ${rate} (not supported by Spotify SDK)`);
        }
      } catch (err) {
        logger.error("Error setting playback rate:", err);
      }
    }
  }, [state.platform, setPlaybackOption, playerInstanceRef]);

  return {
    setPlaybackState,
    fadeVolume,
    handlePlayPause,
    handleNextTrack,
    handlePrevTrack,
    toggleShuffle,
    toggleRepeat,
    setPlaybackRate
  };
}
